import React, { useCallback, useEffect, useState } from 'react';
import Typography from '../../../components/atoms/typography';
import CustomDataGrid from '../../../components/atoms/dataGrid';
import useNearGetCarts, { GetCarts, Row } from '../../../hooks/carts/useNearGetCarts';
import ActivityIndicator from '../../../components/ActivityIndicator';
import { Box } from '@material-ui/core';
import { GridSelectionModel } from '@mui/x-data-grid';
import { useFormContext } from 'react-hook-form';
import { formatDateTime } from '../../../utils/formatTime';
import { FreightDetails } from '../../../components/molecules/import/configureFreight';
import { defaultFormatKilometrageNumbers } from '../../../utils/formatKilometrageNumber';

const columns = [
  {
    field: 'corporateName',
    flex: 1.2,
    editable: false,
    renderHeader: () => (
      <Typography color="gray" fontWeight="700">
        PDV
      </Typography>
    ),
  },
  {
    field: 'distance',
    flex: 1,
    editable: false,
    renderHeader: () => (
      <Typography color="gray" fontWeight="700">
        Distância (Km)
      </Typography>
    ),
  },
  {
    field: 'totalWeight',
    flex: 1,
    editable: false,
    renderHeader: () => (
      <Typography color="gray" fontWeight="700">
        Peso Total (Kg)
      </Typography>
    ),
  },
  {
    field: 'createdAt',
    flex: 1,
    editable: false,
    valueFormatter: (params: { value: string }) => formatDateTime(params.value),
    renderHeader: () => (
      <Typography color="gray" fontWeight="700">
        Criado em
      </Typography>
    ),
  },
  {
    field: 'updatedAt',
    flex: 1,
    editable: false,
    valueFormatter: (params: { value: string }) => formatDateTime(params.value),
    renderHeader: () => (
      <Typography color="gray" fontWeight="700">
        Reprocessado em
      </Typography>
    ),
  },
];

const FreightConfigurationTable: React.FC = () => {
  const { getValues, setValue } = useFormContext<FreightDetails>();
  const freightDetails = getValues();
  const [selectedCartIds, setSelectedCartIds] = useState<string[]>(freightDetails.extraCartIds || []);
  const [selectedCartsData, setSelectedCartsData] = useState<Row[]>(freightDetails.extraCarts || []);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [nearCartsResult, setNearCartsResult] = useState<GetCarts>({
    rows: [],
    totalRecords: 0,
    page: 1,
  });

  const { execute, loading } = useNearGetCarts();

  const setExtraCarts = (carts: Row[]) => setValue('extraCarts', carts, { shouldValidate: true, shouldDirty: true })

  const calculateTotalWeight = (carts: Row[]): number => {
    return carts.reduce((total, cart) => total + Number(cart.totalWeight), 0);
  };

  const handleSelectionChange = async (selectionModel: GridSelectionModel) => {
    const newSelectedCartIds = selectionModel.map(id => id.toString());
    const currentPageCartIds = nearCartsResult.rows.map((cart: Row) => cart.cartId);

    setHasUserInteracted(true);

    const currentPageSelectedCarts = nearCartsResult.rows.filter((cart: Row) =>
      newSelectedCartIds.includes(cart.cartId)
    );

    const cartsNotInCurrentPage = selectedCartsData.filter((cart: Row) =>
      !currentPageCartIds.includes(cart.cartId)
    );
    
    const allSelectedCarts = [...cartsNotInCurrentPage, ...currentPageSelectedCarts];
    const finalSelectedCartIds = allSelectedCarts.map(cart => cart.cartId);

    const newWeight = calculateTotalWeight(allSelectedCarts);
    const previousDeliveries = selectedCartsData.length;
    const newDeliveries = allSelectedCarts.length;
    const deliveriesDifference = newDeliveries - previousDeliveries;

    setValue('totalWeight', freightDetails.originalTotalWeight + newWeight, { 
      shouldValidate: true, 
      shouldDirty: true 
    });

    setValue('totalDeliveries', Number(freightDetails.totalDeliveries) + deliveriesDifference, { 
      shouldValidate: true, 
      shouldDirty: true 
    });

    setSelectedCartIds(finalSelectedCartIds);
    setSelectedCartsData(allSelectedCarts);
    setExtraCarts(allSelectedCarts);
    
    setValue('extraCartIds', finalSelectedCartIds, { shouldValidate: true, shouldDirty: true });
  };


  const loadCarts = useCallback(async () => {
    const result = await execute({
      page: currentPage + 1,
      rowsPerPage: pageSize,
      freightRequestId: freightDetails.freightRequestId,
    });

    setNearCartsResult({
      ...result,
      rows: result.rows,
    });
  }, [currentPage, pageSize, freightDetails.freightRequestId]);


  useEffect(() => {
    if (!hasUserInteracted && freightDetails.extraCarts && freightDetails.extraCarts.length > 0) {
      const cartIds = freightDetails.extraCarts.map((cart: Row) => cart.cartId);
      setSelectedCartIds(cartIds);
      setSelectedCartsData(freightDetails.extraCarts);
    }
  }, [freightDetails.freightRequestId, hasUserInteracted]);

  useEffect(() => {
    loadCarts();
  }, [loadCarts]);

  if (loading) {
    return <ActivityIndicator />;
  }
  return (
    <>
      {nearCartsResult.rows.length === 0 ?
        (<Box component="div">
          <Typography fontWeight="700" color="secondary">
            Reprocessamento
          </Typography>
          <Typography mt={4} fontWeight="400" color="secondary">
            Não existem entregas reprocessadas disponíveis.
          </Typography>
        </Box>) : (<Box component="div">
          <Typography fontWeight="700" color="secondary">
            Reprocessamento
          </Typography>
          <Typography mt={4} mb={4} fontWeight="400" color="secondary">
            Existem entregas disponíveis para serem reprocessadas, gostaria de incluí-las neste frete?
          </Typography>
          <CustomDataGrid
            columns={columns}
            rows={nearCartsResult.rows.map(el => ({
              ...el,
              totalWeight: Number(el.totalWeight).toLocaleString('pt-BR', {
                unit: 'kilogram',
                style: 'unit',
                maximumFractionDigits: 2,
              }),
              distance: defaultFormatKilometrageNumbers(el.distance),
            }))}
            checkboxSelection
            getRowId={row => row.cartId}
            selectionModel={selectedCartIds.filter(id => 
              nearCartsResult.rows.some(row => row.cartId === id)
            )}
            onSelectionModelChange={handleSelectionChange}
            disableColumnFilter
            disableColumnMenu
            disableColumnSelector
            pageSize={pageSize}
            rowsPerPageOptions={[5, 10, 20, 50]}
            paginationMode="server"
            rowCount={nearCartsResult.totalRecords}
            page={currentPage}
            onPageChange={(newPage: number) => setCurrentPage(newPage)}
            onPageSizeChange={(newPageSize: number) => {
              setPageSize(newPageSize);
              setCurrentPage(0);
            }}
          />
        </Box>)
      }
    </>
  );
};

export default FreightConfigurationTable;

